import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from "react";

/**
 * Persists a piece of UI state (open/closed booleans, active tab indices, etc.)
 * across page refreshes via localStorage. Everything lives under one root key
 * so reads/writes are a single JSON blob — small enough that this is fine.
 *
 * Pass a stable, mission-scoped `key` like `${missionId}:section_evaluation_load_2`.
 */

const ROOT_KEY = "froy.ui.state";

function readStore(): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(ROOT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function writeStore(store: Record<string, unknown>): void {
  try {
    localStorage.setItem(ROOT_KEY, JSON.stringify(store));
  } catch {
    // private mode / quota — silently drop persistence
  }
}

export function usePersistedState<T>(key: string, defaultValue: T): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    const store = readStore();
    if (key in store) return store[key] as T;
    return defaultValue;
  });

  // When the key changes (e.g. user navigates to a different mission), rehydrate
  // from storage so we don't carry the previous key's value into the new context.
  const lastKeyRef = useRef(key);
  useEffect(() => {
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;
    const store = readStore();
    setValue(key in store ? (store[key] as T) : defaultValue);
  }, [key, defaultValue]);

  const setAndPersist = useCallback<Dispatch<SetStateAction<T>>>(
    (next) => {
      setValue((prev) => {
        const resolved = typeof next === "function" ? (next as (p: T) => T)(prev) : next;
        const store = readStore();
        if (Object.is(store[key], resolved)) return resolved;
        store[key] = resolved;
        writeStore(store);
        return resolved;
      });
    },
    [key],
  );

  return [value, setAndPersist];
}
